import { isEmpty } from 'lodash';

import type { AppService } from 'app/developer-v3/types';
import type { AppDefinitionSchema } from 'app/developer-v3/platformSchema/app-definition';
import type { ZapData } from 'app/entities/Zap/types';
import type { TaskData } from 'app/developer-v3/types/task';
import { CliAppDefinitionData } from 'app/entities/CliAppDefinition/types';

type ZapDetail = {
  hasTask: boolean;
  hasZap: boolean;
  key: string;
  name: string;
  type: 'triggers' | 'creates' | 'searches';
};

const matchZaps = (
  selectedApi: string,
  definition: AppDefinitionSchema,
  zaps: Array<ZapData>,
  types: Array<'triggers' | 'creates' | 'searches'>
) => {
  return types.flatMap(type => {
    const schema = definition[type];

    if (!schema) {
      return [];
    }

    return Object.entries(schema).map(([key, def]: [string, AppService]) => {
      const matchedZap = zaps.find(
        ({ nodes }) =>
          !isEmpty(nodes) &&
          nodes.some(
            node => node.selected_api === selectedApi && node.action === key
          )
      );

      return {
        key,
        name: def.display.label,
        zapId: matchedZap?.id,
        type,
      };
    });
  });
};

function makeCreateDetails(tasks: TaskData[]) {
  const hasMatchingTask = (zapId: ID) =>
    tasks.some(task => task.root_id === zapId);

  return detail => {
    return {
      key: detail.key,
      name: detail.name,
      hasZap: !!detail.zapId,
      hasTask: !!detail.zapId && hasMatchingTask(detail.zapId),
      type: detail.type,
    };
  };
}

/**
 * Gives a summary of an implementation's Zap/Task data
 * (ie. Show whether each trigger/action have a corresponding Zap and/or Task).
 * If @zaps and @tasks aren't included, we still get the trigger/action detail summary
 */
function parseZapDetails(
  appVersion: CliAppDefinitionData,
  zaps: ZapData[] = [],
  tasks: TaskData[] = []
): { triggers: ZapDetail[]; actions: ZapDetail[] } {
  const selectedApi = appVersion.selected_api;
  const definition = appVersion.definition_override || {};
  const createDetails = makeCreateDetails(tasks);

  const zapMap = {
    triggers: matchZaps(selectedApi, definition, zaps, ['triggers']),
    actions: matchZaps(selectedApi, definition, zaps, ['creates', 'searches']),
  };

  return {
    triggers: zapMap.triggers.map(createDetails),
    actions: zapMap.actions.map(createDetails),
  };
}

export { parseZapDetails };
